import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrderingFormFields } from '@/components/question/forms/ordering/OrderingForm';
import {
  IsNewItemArgs,
  UseQuestionFieldArrayReturn,
} from '@/data/hook/question/answers/types';
import { useSpotlight } from '@/data/hook/shared/useSpotlight';
import { OrderingItemCreateDTO } from '@/models/Answer';
import { validateOrderingAnswers } from '@/utils/questions/validateAnswers';

type UseOrderingItemsReturn =
  UseQuestionFieldArrayReturn<OrderingItemCreateDTO> & {
    handleUpdateValidPositions: (
      index: number,
      validPositions: number[],
    ) => void;
    handleMarkIncorrect: (index: number) => void;
  };

export function useOrderingAnswers(): UseOrderingItemsReturn {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const { control, getValues, setValue } = useFormContext<OrderingFormFields>();
  const {
    fieldState: { error },
  } = useController({ control, name: 'orderingItems' });

  const { focus, clearIfMatches, isFocused } = useSpotlight();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'orderingItems',
    keyName: 'fieldId',
    rules: {
      validate: (items = []) => validateOrderingAnswers(items, t),
    },
  });

  const handleAdd = () => {
    append({ text: t('newOption'), validPositions: [] });
    focus(fields.length);
  };

  const handleUpdateText = (index: number, text: string) => {
    const current = getValues(`orderingItems.${index}`);
    update(index, { ...current, text, mediaFile: null, altText: '' });
    clearIfMatches(index);
  };

  const handleUpdateMedia = (
    index: number,
    mediaFile: File | string,
    altText?: string,
  ) => {
    const current = getValues(`orderingItems.${index}`);
    update(index, {
      ...current,
      mediaFile,
      altText: altText ?? null,
      text: '',
    });
    clearIfMatches(index);
  };

  const handleUpdateValidPositions = (
    index: number,
    validPositions: number[],
  ) => {
    setValue(`orderingItems.${index}.validPositions`, validPositions);
    clearIfMatches(index);
  };

  const handleMarkIncorrect = (index: number) => {
    setValue(`orderingItems.${index}.validPositions`, []);
    clearIfMatches(index);
  };

  const handleDelete = (index: number) => {
    const currentItems = getValues('orderingItems');
    const newTotal = currentItems.length - 1;

    currentItems.forEach((item, i) => {
      if (i === index) return;
      const filtered = item.validPositions.filter(p => p <= newTotal);
      if (filtered.length !== item.validPositions.length) {
        setValue(`orderingItems.${i}.validPositions`, filtered);
      }
    });

    remove(index);
  };

  const isNewItem = ({ index }: IsNewItemArgs) => isFocused(index);

  return {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleUpdateValidPositions,
    handleMarkIncorrect,
    handleDelete,
    isNewItem,
  };
}
